import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { useDiscountRoundingPolicy, usePricingConfig, usePutDiscountRoundingPolicy, useUpsertPricingConfig } from '../../hooks';
import { FAILOVER_POLICY_OPTIONS, PRICING_MODE_OPTIONS } from '../../model/options';
import type { DiscountRoundingMode, FailoverPolicy, PricingMode } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { RoundingPoliciesCard } from './RoundingPoliciesCard';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const DISCOUNT_ROUNDING_MODES: DiscountRoundingMode[] = ['HALF_UP', 'HALF_DOWN', 'HALF_EVEN', 'DOWN', 'UP'];

/** The moved-in-place discount-rounding form (design spec §8.11/§12.4) — cut verbatim from
 * `PriceOperationsPanel` ("Operations" keeps diagnostics only now) into this card, its merchant
 * home. adr-pricing-022: a SEPARATE policy from the price-rounding cards above it. */
function DiscountRoundingCard({ canManage, onResult }: PanelProps) {
  const { t } = useTranslation('catalog');
  const discountPolicy = useDiscountRoundingPolicy();
  const putDiscountPolicy = usePutDiscountRoundingPolicy();
  const [roundingMode, setRoundingMode] = useState<DiscountRoundingMode>('HALF_UP');
  const [scale, setScale] = useState('4');

  useEffect(() => {
    if (!discountPolicy.data) return;
    setRoundingMode(discountPolicy.data.roundingMode);
    setScale(String(discountPolicy.data.scale));
  }, [discountPolicy.data]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t('priceOperationsPanel.discountRounding.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('roundingPanel.discountNote')}
        </Typography>
        {discountPolicy.isError ? <Alert severity="warning" sx={{ mb: 2 }}>{discountPolicy.error?.message}</Alert> : null}
        <Stack spacing={2.5} sx={{ maxWidth: 320 }}>
          <TextField
            select
            label={t('priceOperationsPanel.discountRounding.mode')}
            value={roundingMode}
            onChange={(e) => setRoundingMode(e.target.value as DiscountRoundingMode)}
            disabled={!canManage}
          >
            {DISCOUNT_ROUNDING_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {mode}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('priceOperationsPanel.discountRounding.scale')}
            value={scale}
            onChange={(e) => setScale(e.target.value)}
            type="number"
            disabled={!canManage}
            slotProps={{ htmlInput: { min: 0, max: 4 } }}
          />
          {canManage ? (
            <Button
              variant="contained"
              startIcon={putDiscountPolicy.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              disabled={putDiscountPolicy.isPending}
              onClick={() =>
                putDiscountPolicy.mutate(
                  { roundingMode, scale: Number(scale) },
                  {
                    onSuccess: () => onResult('success', t('priceOperationsPanel.discountRounding.saved')),
                    onError: (error) => onResult('error', error.message),
                  },
                )
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('common:action.save')}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Tenant pricing configuration — engine mode, provider, failover policy, plus the rounding-policy
 * cards (design spec §8.11, WP4.3). */
export function PricingConfigPanel({ canManage, onResult }: PanelProps) {
  const { t } = useTranslation('catalog');
  const query = usePricingConfig();
  const save = useUpsertPricingConfig();

  const [mode, setMode] = useState<PricingMode>('INTERNAL');
  const [providerConfigId, setProviderConfigId] = useState('');
  const [failover, setFailover] = useState<FailoverPolicy>('FALLBACK_TO_INTERNAL');

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    setMode((data.pricingMode as PricingMode) ?? 'INTERNAL');
    setProviderConfigId(data.providerConfigId ?? '');
    setFailover((data.failoverPolicy as FailoverPolicy) ?? 'FALLBACK_TO_INTERNAL');
  }, [query.data]);

  // A 404 just means "not configured yet" — show the form with defaults.
  const notConfigured = query.isError && query.error?.status === 404;
  if (query.isLoading) return <LoadingState label={t('pricingConfigPanel.loading')} />;
  if (query.isError && !notConfigured) {
    return (
      <ErrorState
        title={t('pricingConfigPanel.loadError.title')}
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const submit = () => {
    save.mutate(
      {
        pricingMode: mode,
        providerConfigId: providerConfigId.trim() || undefined,
        failoverPolicy: failover,
      },
      {
        onSuccess: () => onResult('success', t('pricingConfigPanel.saved')),
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {t('pricingConfigPanel.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pricingConfigPanel.description')}
          </Typography>
          {notConfigured ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('pricingConfigPanel.notConfigured')}
            </Alert>
          ) : null}
          <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
            <TextField
              select
              label={t('pricingConfigPanel.modeLabel')}
              value={mode}
              onChange={(e) => setMode(e.target.value as PricingMode)}
              disabled={!canManage}
              fullWidth
            >
              {PRICING_MODE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {t(o.label)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('pricingConfigPanel.failoverLabel')}
              value={failover}
              onChange={(e) => setFailover(e.target.value as FailoverPolicy)}
              disabled={!canManage}
              fullWidth
            >
              {FAILOVER_POLICY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {t(o.label)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('pricingConfigPanel.providerConfigIdLabel')}
              value={providerConfigId}
              onChange={(e) => setProviderConfigId(e.target.value)}
              disabled={!canManage}
              fullWidth
              helperText={t('pricingConfigPanel.providerConfigIdHelper')}
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            {canManage ? (
              <Button
                variant="contained"
                onClick={submit}
                disabled={save.isPending}
                startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('common:action.save')}
              </Button>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <RoundingPoliciesCard canManage={canManage} onResult={onResult} />
      <DiscountRoundingCard canManage={canManage} onResult={onResult} />
    </Stack>
  );
}
